import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Check } from "lucide-react";
import { useLang, UI } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUMMER_PROGRAM = "Ligue d'Été Montréal";
const WINTER_PROGRAM = "Winter Cup";
const WC_CATEGORIES = [
  { fr: "U16 Garçons (nouveau)", en: "U16 Boys (new)", es: "U16 Chicos (nuevo)" },
  { fr: "U19 Garçons", en: "U19 Boys", es: "U19 Chicos" },
  { fr: "U23 Garçons", en: "U23 Boys", es: "U23 Chicos" },
  { fr: "Filles 18-23 ans (nouveau)", en: "Girls 18-23 (new)", es: "Chicas 18-23 (nuevo)" },
];
const WEEKS = [
  { fr: "Semaine du 1er juin", en: "Week of June 1", es: "Semana del 1 de junio" },
  { fr: "Semaine du 8 juin", en: "Week of June 8", es: "Semana del 8 de junio" },
  { fr: "Semaine du 15 juin", en: "Week of June 15", es: "Semana del 15 de junio" },
  { fr: "Semaine du 22 juin", en: "Week of June 22", es: "Semana del 22 de junio" },
  { fr: "Semaine du 29 juin", en: "Week of June 29", es: "Semana del 29 de junio" },
  { fr: "Semaine du 6 juillet", en: "Week of July 6", es: "Semana del 6 de julio" },
  { fr: "Semaine du 13 juillet", en: "Week of July 13", es: "Semana del 13 de julio" },
  { fr: "Semaine du 20 juillet", en: "Week of July 20", es: "Semana del 20 de julio" },
  { fr: "Semaine du 27 juillet", en: "Week of July 27", es: "Semana del 27 de julio" },
];

export const ContactForm = ({ defaultProgram, accent = "var(--ms-accent, #E5352B)", programs = [], trips = [] }) => {
  const { t, lang } = useLang();
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    program: defaultProgram || t(UI.form.general), message: "", trip: "", arrival: "", departure: "",
    birthdate: "", category: "",
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const prefix = [
        form.birthdate && `[${t({ fr: "Date de naissance", en: "Date of birth", es: "Fecha de nacimiento" })}: ${form.birthdate}]`,
        form.category && `[${t({ fr: "Catégorie", en: "Category", es: "Categoría" })}: ${form.category}]`,
        form.trip && `[${t({ fr: "Voyage souhaité", en: "Desired trip", es: "Viaje deseado" })}: ${form.trip}]`,
        form.arrival && `[${t({ fr: "Semaine d'arrivée", en: "Arrival week", es: "Semana de llegada" })}: ${form.arrival}]`,
        form.departure && `[${t({ fr: "Semaine de départ", en: "Departure week", es: "Semana de salida" })}: ${form.departure}]`,
      ].filter(Boolean).join("\n");
      const message = prefix ? `${prefix}\n${form.message}` : form.message;
      await axios.post(`${API}/contact`, { name: form.name, email: form.email, phone: form.phone, program: form.program, message, lang });
      setStatus("done");
      setForm({ name: "", email: "", phone: "", program: defaultProgram || t(UI.form.general), message: "", trip: "", arrival: "", departure: "", birthdate: "", category: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-neutral-500 font-body focus:outline-none focus:border-flame transition-colors duration-300";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="contact-name" required value={form.name} onChange={upd("name")}
          placeholder={t(UI.form.name)} className={inputCls} />
        <input data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")}
          placeholder={t(UI.form.email)} className={inputCls} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input data-testid="contact-phone" value={form.phone} onChange={upd("phone")}
          placeholder={t(UI.form.phone)} className={inputCls} />
        {programs.length > 0 ? (
          <select data-testid="contact-program" value={form.program} onChange={upd("program")}
            className={inputCls + " [&>option]:bg-ink"}>
            <option value={t(UI.form.general)}>{t(UI.form.general)}</option>
            {programs.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        ) : (
          <input data-testid="contact-program" value={form.program} onChange={upd("program")}
            placeholder={t(UI.form.program)} className={inputCls} />
        )}
      </div>
      {form.program === WINTER_PROGRAM && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="winter-cup-fields">
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
              {t({ fr: "Date de naissance", en: "Date of birth", es: "Fecha de nacimiento" })}
            </label>
            <input data-testid="contact-birthdate" type="date" required value={form.birthdate} onChange={upd("birthdate")}
              className={inputCls + " [color-scheme:dark]"} />
          </div>
          <div>
            <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
              {t({ fr: "Catégorie", en: "Category", es: "Categoría" })}
            </label>
            <select data-testid="contact-category" required value={form.category} onChange={upd("category")}
              className={inputCls + " [&>option]:bg-ink"}>
              <option value="">{t({ fr: "Choisir une catégorie", en: "Choose a category", es: "Elegir una categoría" })}</option>
              {WC_CATEGORIES.map((c) => <option key={c.fr} value={t(c)}>{t(c)}</option>)}
            </select>
          </div>
        </div>
      )}
      {trips.length > 0 && (
        <select data-testid="contact-trip" value={form.trip} onChange={upd("trip")}
          className={inputCls + " [&>option]:bg-ink"}>
          <option value="">{t({ fr: "Voyage souhaité (optionnel)", en: "Desired trip (optional)", es: "Viaje deseado (opcional)" })}</option>
          {trips.map((x) => {
            const label = `${x.program} — ${t(x.place)} · ${t(x.period)}`;
            return <option key={label} value={label}>{label}</option>;
          })}
        </select>
      )}
      {form.program === SUMMER_PROGRAM && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="weeks-selects">
          <select data-testid="contact-arrival-week" value={form.arrival} onChange={upd("arrival")}
            className={inputCls + " [&>option]:bg-ink"}>
            <option value="">{t({ fr: "Semaine d'arrivée (1er juin → 31 juillet)", en: "Arrival week (June 1 → July 31)", es: "Semana de llegada (1 junio → 31 julio)" })}</option>
            {WEEKS.map((w) => <option key={w.fr} value={t(w)}>{t(w)}</option>)}
          </select>
          <select data-testid="contact-departure-week" value={form.departure} onChange={upd("departure")}
            className={inputCls + " [&>option]:bg-ink"}>
            <option value="">{t({ fr: "Semaine de départ (2 à 8 semaines)", en: "Departure week (2 to 8 weeks)", es: "Semana de salida (2 a 8 semanas)" })}</option>
            {WEEKS.map((w) => <option key={w.fr} value={t(w)}>{t(w)}</option>)}
          </select>
        </div>
      )}
      <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={upd("message")}
        placeholder={t(UI.form.message)} className={inputCls + " resize-none"} />

      <div className="flex items-center gap-6 pt-2">
        <motion.button
          data-testid="contact-submit"
          type="submit"
          disabled={status === "sending"}
          whileTap={{ scale: 0.95 }}
          style={{ backgroundColor: accent }}
          className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-heading font-semibold text-white uppercase tracking-wide text-sm disabled:opacity-60"
        >
          {status === "done" ? <Check size={18} /> : <Send size={18} />}
          {status === "sending" ? t(UI.form.sending) : t(UI.form.send)}
        </motion.button>
        {status === "done" && (
          <span data-testid="contact-success" className="text-green-400 text-sm font-body">{t(UI.form.success)}</span>
        )}
        {status === "error" && (
          <span data-testid="contact-error" className="text-red-400 text-sm font-body">{t(UI.form.error)}</span>
        )}
      </div>
    </form>
  );
};
